/**
 * @file TwoFactorSettings.tsx
 * @brief Profile → Security: two-step verification (B31, ADR-059). Turn on
 *        (QR + setup key + first code), backup codes (shown once, copy /
 *        download, make new ones), trusted devices (forget all), turn off
 *        (when not required). Super Admins also see the staff switch, which
 *        only the Owner can change.
 *
 * @details
 * - Status from GET /api/auth/2fa/status; `useTwoFactorTabVisible` hides the
 *   tab on a backend without 2FA (404).
 * - Making new backup codes takes an app code (not a backup code); turning
 *   off takes either. Required 2FA (Owner, staff with the switch on, a
 *   company that requires it) cannot be turned off: the server refuses with
 *   TWO_FACTOR_REQUIRED_BY_POLICY and the button is not shown.
 * - Backup codes stay in this component's state until "Done"; never cached.
 * - Staff switch: PUT /api/admin/security/two-factor, Owner only; refused
 *   (409 TWO_FACTOR_OWNER_FIRST) until the Owner's own 2FA is on, so the
 *   switch is disabled until then with that reason.
 *
 * @author DES
 * @date 25-10-2026
 */

import { useId, useRef, useState, type ReactNode } from "react";
import { CircleCheck, LoaderCircle, ShieldCheck, ShieldOff } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Switch } from "@/components/ui/Switch";
import { GridBand } from "@/features/analytics/components/GridBlock";
import { BackupCodesPanel } from "@/features/auth/twoFactor/BackupCodesPanel";
import { CodeField, SetupSteps } from "@/features/auth/twoFactor/TwoFactorSteps";
import {
  describeTwoFactorError,
  useConfirmSetup,
  useDisableTwoFactor,
  useForgetTrustedDevices,
  useRegenerateBackupCodes,
  useSaveStaffTwoFactor,
  useStaffTwoFactor,
  useStartSetup,
  useTwoFactorStatus,
  type TwoFactorStatus,
} from "@/features/auth/twoFactor/twoFactorApi";
import { cleanAppCode, cleanBackupCode } from "@/features/auth/twoFactor/twoFactorRules";
import { useAuth } from "@/shared/auth/auth-context";
import { toast } from "@/shared/hooks/useToast";
import { formatDate } from "@/shared/utils/formatters";

const REQUIRED_LINE: Record<string, string> = {
  owner: "Required for the Owner account, so it can't be turned off.",
  superadmin: "Required for VERBLIZR staff, so it can't be turned off.",
  company: "Your company requires it, so it can't be turned off.",
};

const rowClass = "flex items-start gap-3 border-b border-border/60 px-4 py-3.5 md:px-5";

function Row({ title, body, action }: { title: ReactNode; body?: ReactNode; action?: ReactNode }) {
  return (
    <div className={rowClass}>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium">{title}</span>
        {body ? <span className="mt-0.5 block text-xs text-muted-foreground">{body}</span> : null}
      </span>
      {action ? <span className="shrink-0">{action}</span> : null}
    </div>
  );
}

/** A code box under a row, for "make new codes" and "turn off". */
function ProofForm({
  allowBackup,
  submitLabel,
  destructive,
  onSubmit,
  onCancel,
}: {
  allowBackup: boolean;
  submitLabel: string;
  destructive?: boolean;
  onSubmit: (proof: { code?: string; backupCode?: string }) => Promise<void>;
  onCancel: () => void;
}) {
  const [backup, setBackup] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const id = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const clean = backup ? cleanBackupCode(code) : cleanAppCode(code);
    if (!clean) {
      setError(backup ? "Enter one of your backup codes." : "Enter the 6-digit code from your authenticator app.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onSubmit(backup ? { backupCode: clean } : { code: clean });
    } catch (err) {
      setError(describeTwoFactorError(err));
      setCode("");
      setBusy(false);
      window.setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-3 border-b border-border/60 bg-app-page px-4 py-4 md:px-5" noValidate>
      <div className="max-w-sm">
        <CodeField
          key={backup ? "b" : "a"}
          id={id}
          label={backup ? "Backup code" : "App code"}
          backup={backup}
          value={code}
          onChange={setCode}
          disabled={busy}
          inputRef={inputRef}
        />
      </div>
      {error ? <p role="alert" className="text-xs text-destructive">{error}</p> : null}
      <div className="flex flex-wrap items-center gap-2">
        <Button type="submit" size="sm" variant={destructive ? "destructive" : "default"} disabled={busy || !code.trim()}>
          {busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : submitLabel}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        {allowBackup ? (
          <button
            type="button"
            className="ml-auto text-xs font-medium text-brand hover:underline"
            onClick={() => {
              setBackup((v) => !v);
              setCode("");
              setError(null);
            }}
            disabled={busy}
          >
            {backup ? "Use a code from your app" : "Use a backup code"}
          </button>
        ) : null}
      </div>
    </form>
  );
}

type Mode = "idle" | "setup" | "regenerate" | "disable" | { codes: string[] };

export function TwoFactorSettings() {
  const { user } = useAuth();
  const status = useTwoFactorStatus();
  const [mode, setMode] = useState<Mode>("idle");
  const startSetup = useStartSetup();
  const confirmSetup = useConfirmSetup();
  const regenerate = useRegenerateBackupCodes();
  const disable = useDisableTwoFactor();
  const forgetDevices = useForgetTrustedDevices();
  const s = status.data;

  const header = (
    <GridBand className="text-sm font-medium">
      Two-step verification
      <span className="ml-1 font-normal text-muted-foreground">
        · A code from an authenticator app on your phone, as well as your password or Google / Apple sign-in.
      </span>
    </GridBand>
  );

  if (!s) {
    return (
      <div className="border-t border-border/60">
        {header}
        <div className="px-4 py-3.5 text-sm text-muted-foreground md:px-5">
          {status.isError ? "Could not load two-step verification." : "Checking…"}
        </div>
      </div>
    );
  }

  // Backup codes just made (setup or new set): shown once, then gone.
  if (typeof mode === "object") {
    return (
      <div className="border-t border-border/60">
        {header}
        <div className="max-w-md px-4 py-4 md:px-5">
          <BackupCodesPanel codes={mode.codes} accountName={user?.email ?? null} continueLabel="Done" onContinue={() => setMode("idle")} />
        </div>
      </div>
    );
  }

  return (
    <div className="border-t border-border/60">
      {header}
      {!s.enabled ? (
        <OffState
          status={s}
          setupOpen={mode === "setup"}
          onOpen={() => setMode("setup")}
          onCancel={() => setMode("idle")}
          start={() => startSetup.mutateAsync()}
          confirm={async (code) => setMode({ codes: await confirmSetup.mutateAsync(code) })}
        />
      ) : (
        <>
          <Row
            title={
              <span className="inline-flex items-center gap-1.5">
                <CircleCheck className="h-4 w-4 text-success" />
                On
              </span>
            }
            body={
              <>
                {s.enabledAt ? `Since ${formatDate(s.enabledAt)}.` : null}{" "}
                {s.required && s.requiredReason ? REQUIRED_LINE[s.requiredReason] : null}
              </>
            }
          />
          <Row
            title="Backup codes"
            body={
              s.backupCodesLeft != null
                ? `${s.backupCodesLeft} of 10 left. Making new ones stops the old ones working.`
                : "Making new ones stops the old ones working."
            }
            action={
              mode !== "regenerate" ? (
                <Button variant="ghost" size="sm" onClick={() => setMode("regenerate")}>
                  Make new codes
                </Button>
              ) : null
            }
          />
          {mode === "regenerate" ? (
            <ProofForm
              allowBackup={false}
              submitLabel="Make new codes"
              onCancel={() => setMode("idle")}
              onSubmit={async ({ code }) => setMode({ codes: await regenerate.mutateAsync(code!) })}
            />
          ) : null}
          {s.trustDeviceAllowed ? (
            <Row
              title="Trusted devices"
              body={
                s.trustedDevices > 0
                  ? `${s.trustedDevices} ${s.trustedDevices === 1 ? "device skips" : "devices skip"} the code for 30 days.`
                  : "None. Tick “Trust this device for 30 days” when you sign in to skip the code on that device."
              }
              action={
                s.trustedDevices > 0 ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={forgetDevices.isPending}
                    onClick={async () => {
                      try {
                        const n = await forgetDevices.mutateAsync();
                        toast({ title: "Devices forgotten", description: `${n} ${n === 1 ? "device asks" : "devices ask"} for the code again.` });
                      } catch (err) {
                        toast({ variant: "destructive", title: "Couldn't forget the devices", description: describeTwoFactorError(err) });
                      }
                    }}
                  >
                    {forgetDevices.isPending ? <LoaderCircle className="h-4 w-4 animate-spin" /> : "Forget all"}
                  </Button>
                ) : null
              }
            />
          ) : (
            <Row title="Trusted devices" body="Not offered for the Owner account: the code is asked at every sign-in." />
          )}
          {!s.required ? (
            <>
              <Row
                title="Turn off"
                body="Your account is then protected by your password or Google / Apple sign-in only."
                action={
                  mode !== "disable" ? (
                    <Button variant="ghost" size="sm" className="text-destructive" onClick={() => setMode("disable")}>
                      <ShieldOff className="h-4 w-4" />
                      Turn off
                    </Button>
                  ) : null
                }
              />
              {mode === "disable" ? (
                <ProofForm
                  allowBackup
                  destructive
                  submitLabel="Turn off two-step verification"
                  onCancel={() => setMode("idle")}
                  onSubmit={async (proof) => {
                    await disable.mutateAsync(proof);
                    setMode("idle");
                    toast({ title: "Two-step verification is off" });
                  }}
                />
              ) : null}
            </>
          ) : null}
        </>
      )}
      {user?.role === "superadmin" ? <StaffTwoFactorSwitch ownerHasTwoFactor={s.isOwner && s.enabled} isOwner={s.isOwner} /> : null}
    </div>
  );
}

function OffState({
  status,
  setupOpen,
  onOpen,
  onCancel,
  start,
  confirm,
}: {
  status: TwoFactorStatus;
  setupOpen: boolean;
  onOpen: () => void;
  onCancel: () => void;
  start: () => Promise<import("@/shared/auth/auth-context").TwoFactorSetupInfo>;
  confirm: (code: string) => Promise<void>;
}) {
  if (!status.available) {
    return <Row title="Off" body="Two-step verification is not available right now. Please try again later." />;
  }
  return (
    <>
      <Row
        title={
          <span className="inline-flex items-center gap-1.5">
            Off
            {status.required ? <Badge variant="outline">Required</Badge> : null}
          </span>
        }
        body={
          status.required && status.requiredReason
            ? "Your account requires it. Turn it on now, or you'll be asked at your next sign-in."
            : "Turn it on so a stolen password alone can't open your account."
        }
        action={
          !setupOpen ? (
            <Button size="sm" className="gap-1.5" onClick={onOpen}>
              <ShieldCheck className="h-4 w-4" />
              Turn on
            </Button>
          ) : null
        }
      />
      {setupOpen ? (
        <div className="border-b border-border/60 bg-app-page px-4 py-4 md:px-5">
          <div className="max-w-sm">
            <SetupSteps start={start} confirm={confirm} />
            <Button type="button" variant="ghost" className="mt-2 w-full" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </div>
      ) : null}
    </>
  );
}

/**
 * Super Admins: "Require two-step verification for VERBLIZR staff". Only the
 * Owner can change it, and only once the Owner's own 2FA is on (rollout
 * step 5). Everyone else sees it read-only.
 */
function StaffTwoFactorSwitch({ isOwner, ownerHasTwoFactor }: { isOwner: boolean; ownerHasTwoFactor: boolean }) {
  const q = useStaffTwoFactor(true);
  const save = useSaveStaffTwoFactor();
  const d = q.data;
  if (!d) return null;
  const missing = d.staff.filter((m) => !m.twoFactorEnabled);
  const canChange = isOwner && ownerHasTwoFactor && d.available;
  const note = !isOwner
    ? "Only the Owner can change this."
    : !ownerHasTwoFactor
      ? "Turn on your own two-step verification first."
      : !d.available
        ? "Not available right now."
        : null;

  return (
    <>
      <GridBand className="py-2.5">
        <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">VERBLIZR staff</h3>
      </GridBand>
      <div className={rowClass}>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium">Require two-step verification for staff</span>
          <span className="mt-0.5 block text-xs text-muted-foreground">
            The Owner and every Super Admin. Staff without it set it up at their next sign-in.
            {note ? ` ${note}` : ""}
          </span>
        </span>
        <Switch
          checked={d.staffRequired}
          disabled={!canChange || save.isPending}
          aria-label="Require two-step verification for staff"
          className="mt-0.5"
          onCheckedChange={async (v: boolean) => {
            try {
              await save.mutateAsync(v);
              toast({ title: v ? "Staff must use two-step verification" : "Staff two-step verification is optional" });
            } catch (err) {
              toast({ variant: "destructive", title: "Could not save", description: describeTwoFactorError(err) });
            }
          }}
        />
      </div>
      <ul>
        {d.staff.map((m) => (
          <li key={m.id} className="flex items-center gap-3 border-b border-border/60 px-4 py-2.5 text-sm md:px-5">
            <span className="min-w-0 flex-1 truncate">
              {m.name || m.email}
              {m.name ? <span className="ml-1.5 text-xs text-muted-foreground">{m.email}</span> : null}
            </span>
            {m.isOwner ? <Badge variant="primary">Owner</Badge> : null}
            <Badge variant={m.twoFactorEnabled ? "success" : "muted"}>{m.twoFactorEnabled ? "2FA on" : "2FA off"}</Badge>
          </li>
        ))}
      </ul>
      {missing.length > 0 ? (
        <div className="px-4 py-2.5 text-xs text-muted-foreground md:px-5">
          {missing.length} of {d.staff.length} staff not set up yet.
        </div>
      ) : null}
    </>
  );
}
